import React, { useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { CalendarCheck, TriangleAlert } from '../components/icons';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { useGabarit } from '../hooks/useGabarit';
import { interpreterStatut, type EtatPaiement } from '../services/paiementStatut';
import { capturerIncident } from '../services/sentry';
import { colors, screenPadding, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

/**
 * RETOUR DE PAIEMENT — l'écran qu'un TIERS ouvre à notre place.
 *
 * ⚠️ IL NE PREND AUCUN PARAMÈTRE DE ROUTE, et ce n'est pas un oubli. Klarna, Bancontact ou la
 * banque composent l'adresse eux-mêmes : ils n'y mettront jamais ce que react-navigation attendrait.
 * Un `route.params.quelqueChose` lèverait pendant le rendu, démonterait tout l'arbre React et
 * produirait une PAGE ENTIÈREMENT BLANCHE sans rien dans la console — le symptôme déjà rencontré
 * sur `CoachingBooking`, et le plus difficile à diagnostiquer de la série. Un test de mutation
 * garde cette propriété.
 *
 * Tout ce qu'il lit vient de la QUERY, que Stripe remplit : `redirect_status` et
 * `payment_intent_client_secret`. Lot (b) : remplacer `redirect_status` — qui n'est qu'indicatif —
 * par une lecture du PaymentIntent via `stripe-js`, qui fait autorité. Les libellés ne bougeront
 * pas : les deux vocabulaires passent déjà par `interpreterStatut`.
 */

/**
 * Query du navigateur, ou chaîne vide partout ailleurs.
 *
 * ⚠️ `typeof window !== 'undefined'` NE SUFFIT PAS : React Native définit un `window` global
 * SANS `location`. Le test « se rend sans aucune prop » l'a attrapé — `Cannot read properties of
 * undefined (reading 'search')` — c'est-à-dire précisément la page blanche que cet écran doit
 * éviter, sur la plateforme où il ne devrait jamais s'afficher. Chaque niveau est donc vérifié.
 */
function queryCourante(): string {
  if (typeof window === 'undefined') return '';
  return window.location?.search ?? '';
}

/** Lecture de la query, sans dépendre de la plateforme ni d'un paramètre de route. */
export function lireRetour(recherche: string): { etat: EtatPaiement; clientSecret: string | null } {
  const params = new URLSearchParams(recherche);
  return {
    etat: interpreterStatut(params.get('redirect_status')),
    clientSecret: params.get('payment_intent_client_secret'),
  };
}

export interface PaiementRetourScreenProps {
  /** Injectable pour les tests ; par défaut la query réelle du navigateur. */
  recherche?: string;
  onRetourCoaching?: () => void;
}

export function PaiementRetourScreen({ recherche, onRetourCoaching }: PaiementRetourScreenProps = {}) {
  const { large } = useGabarit();
  const { etat, clientSecret } = useMemo(
    () => lireRetour(recherche ?? queryCourante()),
    [recherche],
  );

  // Revenir ici sans le secret signifie une adresse tronquée, recopiée, ou un tiers qui n'a pas
  // renvoyé ce qu'il devait. On n'affiche rien de rassurant et on le remonte : c'est le genre de
  // défaut qui ne se voit jamais autrement.
  // `useEffect` et NON `useMemo` : un effet de bord dans un calcul de rendu peut être exécuté
  // plusieurs fois, ou jamais, au gré de React (rendus rejoués en mode strict, rendu abandonné).
  // L'incident doit partir une fois par arrivée sur l'écran.
  useEffect(() => {
    if (!clientSecret) capturerIncident('coaching_retour_sans_secret', 'paiement');
  }, [clientSecret]);

  const vue = {
    succeeded: {
      testID: 'paiement-retour-succes',
      titre: t('coaching.retourSuccesTitre'),
      corps: t('coaching.retourSuccesCorps'),
      icone: <CalendarCheck size={28} color={colors.success} />,
    },
    // NI SUCCÈS NI ÉCHEC. Le dire autrement pousserait soit à repayer un paiement en cours, soit
    // à promettre un créneau qui peut encore être refusé.
    processing: {
      testID: 'paiement-retour-en-cours',
      titre: t('coaching.retourEnCoursTitre'),
      corps: t('coaching.retourEnCoursCorps'),
      icone: <CalendarCheck size={28} color={colors.info} />,
    },
    failed: {
      testID: 'paiement-retour-echec',
      titre: t('coaching.retourEchecTitre'),
      corps: t('coaching.retourEchecCorps'),
      icone: <TriangleAlert size={28} color={colors.error} />,
    },
    canceled: {
      testID: 'paiement-retour-annule',
      titre: t('coaching.retourAnnuleTitre'),
      corps: t('coaching.retourAnnuleCorps'),
      icone: <TriangleAlert size={28} color={colors.grey500} />,
    },
  }[etat];

  return (
    <SafeAreaView testID="paiement-retour" style={styles.container} edges={['top', 'bottom']}>
      <View style={[styles.content, large && styles.contentL]}>
        <Card>
          <View style={styles.entete}>
            {vue.icone}
            <Text testID={vue.testID} style={styles.titre}>
              {vue.titre}
            </Text>
          </View>
          <Text style={styles.corps}>{vue.corps}</Text>
        </Card>
        <Button
          title={t('coaching.retourVersCoaching')}
          onPress={() => onRetourCoaching?.()}
          testID="paiement-retour-continuer"
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  content: { flex: 1, justifyContent: 'center', paddingHorizontal: screenPadding, gap: spacing.lg },
  // Écran large : une carte lisible au centre, pas une bande de 1200 px.
  contentL: { width: '100%', maxWidth: 520, alignSelf: 'center' },
  entete: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm },
  titre: { flex: 1, fontSize: typography.h3.size, fontWeight: '800', color: colors.night },
  corps: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },
});
